import type { ReactNode } from 'react';
import { Pressable, StyleSheet, Text, View } from 'react-native';
import { Icon, IconButton } from '../index';

import type { useTheme } from './theme';
import { rowIdOf } from './state/loggerDraft';
import type { GroupMember } from './supersetNotebook';
import { useTransientTip } from './useTransientTip';

// SupersetTabs — THE action bar: the first, always-present row of the
// FocusPanel card. It holds two things in a stable layout the user learns once
// (Antenore, inbox 7d3e33dc + 58aaf191, dal mockup):
//   • LEFT — the superset affordance. Standalone → a compact "＋ Superset"
//     pill (create/join). Grouped → a marking CUE (loop glyph + "N/M" position)
//     that reads "you're in a superset, exercise N of M". No group NAME, no
//     rename, no nav arrow on the line (the exercise name is the row below;
//     the superset has no visible name, so rename was removed entirely, AP-0n)
//     — tapping the cue cycles to the next member, the arrow's job without the
//     arrow's footprint.
//   • RIGHT — the exercise action cluster ⇄ replace / ❓ how-to / 🗑 remove, in
//     EVERY card state so the user never hunts for them when the state flips,
//     plus the ☰ superset menu only when grouped. These moved out of the old
//     FocusPanel title row (AP-0g), giving the exercise name its own line.
//
// The screen auto-advances A→B→… after each LOG SET; the cue tap is the manual
// override. State (done/in-progress/not-done) comes from groupMembersOf — data
// model untouched.
export function SupersetTabs({
  members,
  activeEntryId,
  groupLabel,
  resolveName,
  onSelect,
  onSplit,
  onCreate,
  onOpenMenu,
  editLocked,
  onSwap,
  onOpenTip,
  onRemoveExercise,
  hideInert = false,
  t,
  theme,
}: {
  /** Group members (≥1) when grouped; empty for a standalone exercise. */
  members: GroupMember[];
  /** WHICH MEMBER ROW is in focus — DraftEntry.entryId (LG-39). A circuit may
   *  legitimately hold the same exercise twice, so the slug cannot say which
   *  of them the cue is sitting on. */
  activeEntryId: string | null;
  /** Localized group label, e.g. "Superset 1" (from ctx.groupLabelFor) — used
   *  in the cue's a11y label only; not shown on the line. */
  groupLabel: string;
  resolveName: (slug: string, fallback: string) => string;
  onSelect: (entryId: string) => void;
  /** Splits/dissolves the group (☰ fallback when onOpenMenu is undefined). */
  onSplit: () => void;
  /** Standalone only: starts a superset. Always defined on a standalone
   *  exercise (AP-0k — the ＋ Superset pill must be discoverable even with one
   *  exercise); the screen routes it to the group picker when a partner exists,
   *  else the exercise picker to add one first. */
  onCreate?: () => void;
  /** The ☰ hamburger → open the superset management menu (save-template / add /
   *  reorder / remove / dissolve). Falls back to onSplit when undefined. */
  onOpenMenu?: () => void;
  /** Editing a past session: creating + dissolving a superset are forbidden
   *  (＋/☰ greyed + balloon). Member navigation + replace/remove stay live. */
  editLocked: boolean;
  /** ⇄ Replace the active exercise (opens the swap picker). */
  onSwap: () => void;
  /** ❓ Open the how-to quick-tip for the active exercise. */
  onOpenTip: () => void;
  /** 🗑 Remove the active exercise from the workout (parent confirms). */
  onRemoveExercise: () => void;
  /** PE-24 — prescribe authoring only: swap/tip open modals PrescribeTree
   *  does not mount (usePrescribeMount.ts), and ＋Superset/☰ open the
   *  group-picker/superset-menu, also unmounted there. Hides all four
   *  rather than leaving controls that tap to nothing — the cue-cycle
   *  (grouped `left`) and 🗑 remove are untouched, they work today.
   *  'log'/'log-past' never pass it (default false). */
  hideInert?: boolean;
  t: (k: string, opts?: Record<string, unknown>) => string;
  theme: ReturnType<typeof useTheme>;
}) {
  const styles = makeStyles(theme);
  const { showTip, tipNode } = useTransientTip(theme);
  const createLockMsg = t('mobile.timeline.createGroupLocked', {
    defaultValue: "Editing a past session: you can't create a superset.",
  });
  const splitLockMsg = t('mobile.timeline.splitGroupLocked', {
    defaultValue: "Editing a past session: you can't dissolve a superset.",
  });

  const grouped = members.length > 0;

  // Right-side action cluster — ⇄/❓/🗑 shared by every state via the shared
  // IconButton (no hand-rolled box). gap s3 + hitSlop {left,right:4} → 44pt
  // targets that meet exactly, no overlap (the retired title row's recipe); the
  // outer edges (⇄ left, 🗑 right in standalone) face free space so they take
  // the fuller 8 (a11y audit AP-0g).
  const clusterIcons = (
    <>
      {hideInert ? null : (
        <IconButton
          name="arrow-left-right"
          onPress={onSwap}
          accessibilityLabel={t('mobile.logger.replace', {
            defaultValue: 'Replace',
          })}
          hitSlop={{ top: 8, bottom: 8, left: 8, right: 4 }}
          theme={theme}
        />
      )}
      {hideInert ? null : (
        <IconButton
          name="help"
          onPress={onOpenTip}
          accessibilityLabel={t('mobile.logger.descriptionToggle', {
            defaultValue: 'Show / hide description',
          })}
          hitSlop={{ top: 8, bottom: 8, left: 4, right: 4 }}
          theme={theme}
        />
      )}
      <IconButton
        name="trash"
        variant="destructive"
        onPress={onRemoveExercise}
        accessibilityLabel={t('workout.removeExercise', {
          defaultValue: 'Remove exercise',
        })}
        hitSlop={{ top: 8, bottom: 8, left: 4, right: grouped ? 4 : 8 }}
        theme={theme}
      />
    </>
  );

  // Left affordance.
  let left: ReactNode = null;
  if (!grouped) {
    // Standalone: the ＋ Superset pill (or nothing when there's no partner to
    // pair with — the cluster still needs to render, so we can't bail early).
    left = onCreate && !hideInert ? (
      <Pressable
        accessibilityRole="button"
        accessibilityState={{ disabled: editLocked }}
        accessibilityLabel={t('mobile.logger.addGroup', {
          defaultValue: 'Group with another exercise',
        })}
        onPress={editLocked ? () => showTip(createLockMsg) : onCreate}
        hitSlop={{ top: 8, bottom: 8 }}
        style={({ pressed }) => [
          styles.createLine,
          editLocked && styles.lineLocked,
          pressed && !editLocked && styles.createLinePressed,
        ]}
      >
        <Text style={[styles.createText, editLocked && styles.createTextLocked]}>
          {t('mobile.group.groupCta', { defaultValue: '+ Superset' })}
        </Text>
      </Pressable>
    ) : null;
  } else {
    const activePos = Math.max(
      0,
      members.findIndex((m) => rowIdOf(m.entry) === activeEntryId),
    );
    // Cycle forward, wrapping to the first member; null for a lone member.
    const cycleNext =
      members.length > 1 ? members[(activePos + 1) % members.length] : null;
    left = (
      <Pressable
        accessibilityRole="button"
        accessibilityLabel={
          cycleNext
            ? t('mobile.superset.cueCycle', {
                defaultValue:
                  '{{group}}, exercise {{pos}} of {{total}}. Tap to go to {{next}}.',
                group: groupLabel,
                pos: activePos + 1,
                total: members.length,
                next: resolveName(
                  cycleNext.entry.exerciseId,
                  cycleNext.entry.exerciseName,
                ),
              })
            : t('mobile.superset.cueStatic', {
                defaultValue: '{{group}}, exercise {{pos}} of {{total}}',
                group: groupLabel,
                pos: activePos + 1,
                total: members.length,
              })
        }
        disabled={!cycleNext}
        onPress={
          cycleNext ? () => onSelect(rowIdOf(cycleNext.entry)) : undefined
        }
        hitSlop={{ top: 8, bottom: 8, left: 8, right: 4 }}
        style={({ pressed }) => [
          styles.cue,
          pressed && cycleNext ? styles.cuePressed : null,
        ]}
      >
        <Icon
          name="repeat"
          size={theme.typography.sizes.md}
          color={theme.palette.ink}
        />
        <Text style={styles.cueCount}>
          {activePos + 1}/{members.length}
        </Text>
      </Pressable>
    );
  }

  return (
    <>
      {tipNode}
      <View style={styles.bar}>
        {left}
        <View style={styles.spacer} />
        <View style={styles.cluster}>
          {clusterIcons}
          {grouped && !hideInert ? (
            <IconButton
              name="menu"
              locked={editLocked}
              onPress={
                editLocked
                  ? () => showTip(splitLockMsg)
                  : (onOpenMenu ?? onSplit)
              }
              accessibilityLabel={t('mobile.superset.menuOpen', {
                defaultValue: 'Superset options',
              })}
              // Spoken lock reason (the ☰ stays an enabled button for a11y —
              // it's tappable-to-explain, not disabled).
              accessibilityHint={editLocked ? splitLockMsg : undefined}
              hitSlop={{ top: 8, bottom: 8, left: 4, right: 8 }}
              theme={theme}
            />
          ) : null}
        </View>
      </View>
    </>
  );
}

function makeStyles(theme: ReturnType<typeof useTheme>) {
  const { palette: p, spacing: s, radius: r, typography: ty } = theme;
  return StyleSheet.create({
    // The action bar: left affordance + spacer + right cluster, one row, no
    // surrounding card (it lives inside the FocusPanel card).
    bar: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: s.s2,
    },
    spacer: { flex: 1 },
    // gap s3: each cluster icon's 4px horizontal hitSlop meets its neighbour's
    // exactly (44pt targets, zero overlap — the retired title-row recipe).
    cluster: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: s.s3,
    },

    // Grouped CUE — loop glyph + N/M position. Same 36-box treatment as the
    // IconButtons beside it (surface2 + borderStrong, DS-9) so the bar reads as
    // one control family; the `repeat` glyph is the "superset" marker (a SHAPE
    // cue, never colour-only), the count is neutral ink to dodge brand-on-fill
    // contrast debt (DS-11).
    cue: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: s.s1,
      minHeight: 36,
      paddingHorizontal: s.s2,
      borderRadius: r.r2,
      borderWidth: 1,
      borderColor: p.borderStrong,
      backgroundColor: p.surface2,
    },
    cuePressed: { backgroundColor: p.surface },
    cueCount: {
      color: p.ink,
      fontSize: ty.sizes.sm,
      fontWeight: '700',
      fontVariant: ['tabular-nums'],
    },

    // Standalone ＋ Superset — a COMPACT dashed pill (AP-0d, dal mockup: the
    // full-width banner shouted louder than the data rows below it). Hugs its
    // text, same 36 box the qualifier chips use.
    createLine: {
      minHeight: 36,
      justifyContent: 'center',
      paddingHorizontal: s.s3,
      borderRadius: r.r2,
      borderWidth: 1,
      borderStyle: 'dashed',
      borderColor: p.border,
      backgroundColor: p.surface,
    },
    createLinePressed: { backgroundColor: p.surface2 },
    // base, not sm: legibility beats compactness on the label itself (the pill
    // already shrank around it). brand500-on-surface contrast is a token-level
    // issue tracked in the backlog (DS-11), not a per-site fix.
    createText: { color: p.brand500, fontSize: ty.sizes.base, fontWeight: '700' },

    // Edit-locked ＋ Superset: greyed but still Pressable (tap → balloon).
    lineLocked: { opacity: 0.4 },
    createTextLocked: { color: p.ink3 },
  });
}
